import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../../api/client';
import { extractErr } from '../../api/error-message';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { SkeletonRows } from '../../components/skeleton';
import { Symbol } from '../../components/symbol';
import { Toggle } from '../../components/toggle';
import { useConfirm } from '../../components/confirm-dialog';
import { TableSearch, useTableSearch } from '../../components/table-search';
import { useAuthStore } from '../../store/auth-store';
import { toast } from 'sonner';

interface AdminRole {
  id: string;
  name: string;
  description?: string;
  permissions?: Array<{ permissionKey: string }>;
}

type Tab = 'users' | 'roles';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  roleId: string;
  isActive: boolean;
  /** An authenticator app is enrolled. */
  totpEnabled: boolean;
  /** Sign-in asks for the code (or for enrolment when none is set up). */
  twoFactorRequired: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  role: { id: string; name: string };
}

/** The signed-in admin's id, from the access token's `sub` claim. */
function actorIdFromToken(token: string | undefined): string | null {
  try {
    const payload = token?.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const sub = (JSON.parse(json) as { sub?: unknown }).sub;
    return sub == null ? null : String(sub);
  } catch {
    return null;
  }
}

const lastLoginLabel = (u: AdminUser) => (u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—');

export function AdminsPage() {
  const { confirm } = useConfirm();
  const myId = actorIdFromToken(useAuthStore((s) => s.session?.accessToken));
  const [busyId, setBusyId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('users');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [allPermissions, setAllPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r, p] = await Promise.all([
        api.get<AdminUser[]>('/admin/admin-users'),
        api.get<AdminRole[]>('/admin/admin-roles'),
        api.get<string[]>('/admin/permissions'),
      ]);
      setUsers(u.data);
      setRoles(r.data);
      setAllPermissions(p.data);
    } catch (err) {
      setError(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  // Search over every column the users table shows; the whole list is loaded.
  const { query, setQuery, filtered } = useTableSearch(users, (u) => [
    u.name,
    u.email,
    u.role.name,
    u.twoFactorRequired ? 'Required' : 'Off',
    u.totpEnabled ? 'Authenticator set up' : 'Not set up',
    u.isActive ? 'Active' : 'Inactive',
    lastLoginLabel(u),
    u.id === myId ? 'You' : null,
  ]);

  const toggleActive = async (user: AdminUser) => {
    const ok = user.isActive
      ? await confirm({
          title: `Deactivate ${user.name}?`,
          description: 'They’re signed out straight away and can’t sign in until reactivated. Their audit history is kept.',
          confirmLabel: 'Deactivate admin',
          tone: 'danger',
        })
      : await confirm({
          title: `Reactivate ${user.name}?`,
          description: `They’ll be able to sign in again with their ${user.role.name} permissions.`,
          confirmLabel: 'Reactivate admin',
          tone: 'primary',
        });
    if (!ok) return;
    setBusyId(user.id);
    try {
      if (user.isActive) {
        await api.delete(`/admin/admin-users/${user.id}`);
      } else {
        await api.put(`/admin/admin-users/${user.id}`, { isActive: true });
      }
      toast.success(`${user.name} ${user.isActive ? 'deactivated' : 'reactivated'}`);
      await load();
    } catch (err) {
      setError(extractErr(err));
    } finally {
      setBusyId(null);
    }
  };

  // Per-admin sign-in code switch (PUT /admin/admin-users/:id/two-factor).
  const setTwoFactor = async (user: AdminUser, required: boolean) => {
    const self = user.id === myId;
    const ok = required
      ? await confirm({
          title: self ? 'Require two-factor for your account?' : `Require two-factor for ${user.name}?`,
          description: user.totpEnabled
            ? `${self ? 'You’ll' : 'They’ll'} be asked for the 6-digit code from ${self ? 'your' : 'their'} authenticator app from the next sign-in.`
            : self
              ? 'You have no authenticator set up, so your sessions end now: you’ll be signed out at your next refresh and asked to set one up when you sign back in.'
              : `${user.name} has no authenticator set up, so they’re signed out now and asked to set one up at their next sign-in.`,
          confirmLabel: 'Require two-factor',
          tone: 'primary',
        })
      : await confirm({
          title: self ? 'Turn off two-factor for your account?' : `Turn off two-factor for ${user.name}?`,
          description: `${self ? 'You’ll' : 'They’ll'} sign in with email and password only. Anyone with ${self ? 'your' : 'their'} password can get in.${
            user.totpEnabled ? ' The authenticator stays set up, so turning it back on needs no new QR scan.' : ''
          }`,
          confirmLabel: 'Turn off two-factor',
          tone: 'danger',
        });
    if (!ok) return;
    setBusyId(user.id);
    try {
      const { data } = await api.put<AdminUser>(`/admin/admin-users/${user.id}/two-factor`, { required });
      setUsers((list) => list.map((u) => (u.id === data.id ? { ...u, ...data } : u)));
      toast.success(
        required
          ? `Two-factor required for ${self ? 'your account' : user.name}`
          : `Two-factor turned off for ${self ? 'your account' : user.name}`,
      );
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Admin management"
        actions={
          tab === 'users' && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="btn btn-primary"
            >
              <Symbol name="add" size={18} /> New admin
            </button>
          )
        }
      />

      {/* Tab bar */}
      <div className="tabs" role="tablist">
        {(['users', 'roles'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className="tab"
          >
            {t === 'users' ? 'Users' : 'Roles & permissions'}
          </button>
        ))}
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {tab === 'users' && showCreate && (
        <CreateAdminForm
          roles={roles}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); void load(); }}
        />
      )}

      {tab === 'users' && (
        <TableSearch
          value={query}
          onChange={setQuery}
          placeholder="Search by name, email, role, two-factor or status"
          resultCount={filtered.length}
        />
      )}

      {loading ? (
        <div className="panel overflow-hidden"><SkeletonRows cols={6} /></div>
      ) : tab === 'users' ? (
        <div className="panel overflow-hidden">
          {users.length === 0 ? (
            <EmptyState bare icon="admin_panel_settings" title="No admin users found" body="Add one with New admin." />
          ) : filtered.length === 0 ? (
            <EmptyState
              bare
              icon="search_off"
              title={`No matches for “${query.trim()}”`}
              body="Try a name, email or role."
              action={
                <button type="button" onClick={() => setQuery('')} className="btn btn-secondary btn-sm">
                  Clear search
                </button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Two-factor</th>
                    <th>Status</th>
                    <th>Last login</th>
                    <th className="!text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => {
                    const self = u.id === myId;
                    return (
                      <tr key={u.id}>
                        <td className="font-medium text-ink-900">
                          <span className="flex items-center gap-2">
                            {u.name}
                            {self && <span className="badge badge-info">You</span>}
                          </span>
                        </td>
                        <td className="text-ink-700">{u.email}</td>
                        <td>
                          <span className="badge badge-accent">{u.role.name}</span>
                        </td>
                        <td>
                          <div className="flex items-center gap-3">
                            <Toggle
                              checked={u.twoFactorRequired}
                              disabled={busyId === u.id}
                              onChange={(next) => void setTwoFactor(u, next)}
                              aria-label={`Require two-factor for ${self ? 'your account' : u.name}`}
                            />
                            <div>
                              {u.twoFactorRequired
                                ? <span className="badge badge-success">Required</span>
                                : <span className="badge badge-warning">Off</span>}
                              <p className="mt-0.5 text-[12.5px] text-ink-500">
                                {u.totpEnabled ? 'Authenticator set up' : 'Not set up'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td>
                          {u.isActive
                            ? <span className="badge badge-success">Active</span>
                            : <span className="badge badge-danger">Inactive</span>}
                        </td>
                        <td className="text-[13.5px] text-ink-500">{lastLoginLabel(u)}</td>
                        <td className="text-right">
                          {/* The API refuses self-deactivation; don't offer it. */}
                          {self && u.isActive ? (
                            <span className="text-[12.5px] text-ink-500">—</span>
                          ) : (
                            <button
                              type="button"
                              disabled={busyId === u.id}
                              onClick={() => void toggleActive(u)}
                              className={`btn btn-sm ${u.isActive ? 'btn-danger-outline' : 'btn-secondary'}`}
                            >
                              {u.isActive ? 'Deactivate' : 'Reactivate'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* Roles & permissions tab */
        <div className="space-y-6">
          {roles.map((role) => {
            const perms = new Set((role.permissions ?? []).map((p) => p.permissionKey));
            const groups: Record<string, string[]> = {};
            for (const key of allPermissions) {
              const domain = key.split('.')[0];
              (groups[domain] ??= []).push(key);
            }
            return (
              <div key={role.id} className="panel p-6">
                <div className="mb-1 flex items-center gap-3">
                  <h3 className="text-[16px] font-semibold text-ink-900">{role.name}</h3>
                  {role.name === 'SUPER_ADMIN' && (
                    <span className="badge badge-accent">All permissions</span>
                  )}
                </div>
                {role.description && (
                  <p className="mb-4 text-[13.5px] text-ink-500">{role.description}</p>
                )}
                {role.name !== 'SUPER_ADMIN' && (
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[13.5px] md:grid-cols-3 lg:grid-cols-4">
                    {Object.entries(groups).map(([domain, keys]) => (
                      <div key={domain} className="mb-3">
                        <div className="mb-1 text-[12.5px] font-semibold text-ink-500">
                          {domain.charAt(0).toUpperCase() + domain.slice(1)}
                        </div>
                        {keys.map((key) => (
                          <div key={key} className="flex items-center gap-1.5">
                            {perms.has(key)
                              ? <Symbol name="check" size={16} className="text-success-ink" />
                              : <span className="w-4 text-center text-ink-300">—</span>}
                            <span className={perms.has(key) ? 'text-ink-700' : 'text-ink-400'}>{key.split('.').slice(1).join('.')}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {roles.length === 0 && (
            <EmptyState icon="shield_person" title="No roles defined" />
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Create form ------------------------------------------------------

function CreateAdminForm(props: {
  roles: AdminRole[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState(props.roles[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/admin/admin-users', { name, email, password, roleId });
      props.onCreated();
    } catch (err) {
      setError(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel p-6">
      <h2 className="mb-4 text-[16px] font-semibold text-ink-900">Create admin</h2>
      {/* Single column on phones — side-by-side text inputs below ~640px leave
          neither field wide enough to read what you typed. */}
      <form onSubmit={onSubmit} className="grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name" value={name} onChange={setName} required />
        <Field label="Email" type="email" value={email} onChange={setEmail} required />
        <Field label="Password" type="password" value={password} onChange={setPassword} required />
        <label className="block">
          <span className="field-label mb-1.5">Role</span>
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            className="input"
          >
            {props.roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </label>
        {error && <div className="sm:col-span-2"><ErrorBanner message={error} /></div>}
        <div className="mt-2 flex gap-2.5 sm:col-span-2">
          <button
            type="submit"
            disabled={busy || !name || !email || !password}
            className="btn btn-primary"
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
          <button type="button" onClick={props.onClose} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- Helpers ----------------------------------------------------------

function Field(props: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean }) {
  return (
    <label className="block">
      <span className="field-label mb-1.5">{props.label}</span>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        required={props.required}
        className="input"
      />
    </label>
  );
}

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss?: () => void }) {
  return (
    <div role="alert" className="alert alert-danger items-center justify-between">
      <span className="flex items-center gap-2"><Symbol name="error" size={20} />{message}</span>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="btn btn-quiet btn-icon btn-sm !text-current">
          <Symbol name="close" size={18} />
        </button>
      )}
    </div>
  );
}
